"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Alert } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeftOutlined, ArrowRightOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import uploadIcon from "../../../assets/images/upload.png";
import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";

import { registerDoctor } from "../../../authDoctor/redux/thunks/doctorsThunks";
import { useRegisterForm } from "../../hooks/useRegisterForm";

import "../../../authPatient/pages/SignupPatient/SignupPatient.css";
import "./RegisterForm4.css";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
// doit correspondre à la durée de transition CSS (0.5s)
const SWAP_DURATION = 500;
// clé localStorage partagée entre toutes les pages auth (patient/doctor)
const SWAP_STORAGE_KEY = "authHeroOnLeft";
// layout d'origine de cette page : hero à gauche (classe signup-doctor)
const DEFAULT_HERO_ON_LEFT = true;

/**
 * Enregistre explicitement email + mot de passe dans le gestionnaire
 * de mots de passe du navigateur (l'identifiant = email, jamais le téléphone).
 */
const saveCredentialsToBrowser = async ({ email, password, name }) => {
  try {
    if (
      email &&
      password &&
      typeof window !== "undefined" &&
      window.PasswordCredential &&
      navigator.credentials?.store
    ) {
      const cred = new window.PasswordCredential({
        id: email,
        password,
        name: name || email,
      });
      await navigator.credentials.store(cred);
    }
  } catch (e) {
    // non bloquant : le navigateur peut ne pas supporter l'API
    console.warn("Credential store not available:", e);
  }
};

export default function RegisterForm4() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { form } = useRegisterForm();
  const { t } = useTranslation();

  const [licenceImg] = useState(location.state?.licenceImg || null);
  const [identityImg, setIdentityImg] = useState(null);
  const [identityPreviewUrl, setIdentityPreviewUrl] = useState(null);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // ── état persistant de la position des cartes (hero à gauche ou à droite) ──
  const initialHeroOnLeft = (() => {
    if (typeof location.state?.heroOnLeft === "boolean") {
      return location.state.heroOnLeft;
    }
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? DEFAULT_HERO_ON_LEFT : stored === "true";
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);

  // ── refs + état pour le swap des 2 cards (form <-> hero) ──
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  const handleContinueAsPatient = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate("/LoginPatient", { state: { heroOnLeft: newHeroOnLeft } });
    }, SWAP_DURATION);
  };

  const validateFile = useCallback(
    (f) => {
      if (!f) return t("signup.doctor.fileErrorNone");
      if (!["image/jpeg", "image/png"].includes(f.type))
        return t("signup.doctor.fileErrorType");
      if (f.size > MAX_FILE_SIZE) return t("signup.doctor.fileErrorSize");
      return null;
    },
    [t]
  );

  const applyIdentity = (f) => {
    const err = validateFile(f);
    setError(err);
    setIdentityImg(err ? null : f);
  };

  const onChangeIdentity = (e) => {
    const f = e.target.files?.[0];
    if (f) applyIdentity(f);
    e.target.value = "";
  };

  const onDropIdentity = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) applyIdentity(f);
  };

  const onDragOver = (e) => e.preventDefault();

  const handleRemoveIdentity = () => {
    setIdentityImg(null);
    setError(null);
  };

  useEffect(() => {
    if (!identityImg) {
      setIdentityPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(identityImg);
    setIdentityPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [identityImg]);

  const isValid = !!identityImg && !!licenceImg;

  const handleSubmit = async () => {
    setError(null);
    if (!isValid) {
      setError(t("signup.doctor.imagesError"));
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError(t("signup.doctor.passwordsDontMatch"));
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      [
        "fullName",
        "email",
        "phone",
        "password",
        "confirmPassword",
        "specialty",
        "experience",
        "practicePlace",
        "country",
        "city",
        "clinicAddress",
      ].forEach((k) => formData.append(k, form[k]?.toString() || ""));

      formData.append("licenceImg", licenceImg);
      formData.append("identityImg", identityImg);

      await dispatch(registerDoctor(formData)).unwrap();

      // Mémorise email + mot de passe (PAS le téléphone) dans le navigateur
      await saveCredentialsToBrowser({
        email: form.email,
        password: form.password,
        name: form.fullName,
      });

      // Retour à LoginDoctor avec email + mot de passe pré-remplis
      navigate("/LoginDoctor", {
        state: {
          email: form.email,
          password: form.password,
          heroOnLeft,
        },
      });
    } catch (err) {
      const backendMsg = typeof err === "string"
        ? err
        : err?.response?.data?.message || err?.message;
      setError(backendMsg || t("signup.doctor.registrationServerError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container signup-doctor-auth rf4-page">
      <div
        className={`signup-background signup-doctor ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        <aside
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-glow" />
          <button
            type="button"
            className="signup-back-home signup-back-home-doctor signup-hero-back-btn"
            onClick={() => navigate("/")}
            aria-label={t("common.backToHome", "Back to home page")}
          >
            <ArrowLeftOutlined />
            <span>{t("common.backToHome", "Back to home page")}</span>
          </button>
          <div className="signup-hero-top-icon">
            <img src={icon1} alt="" />
          </div>

          <div className="signup-hero-inner">
            <h2 className="signup-hero-title">
              {t(
                "signup.createAccountHeroTitle",
                "Are you signing up as a patient? This registration is for doctors only."
              )}
            </h2>
            <p className="signup-hero-sub">
              {t(
                "signup.doctor.step4HeroText",
                "To book appointments and access care, please create a patient account instead."
              )}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleContinueAsPatient}
            >
              <span>{t("signup.continueAsPatient", "Continue as a patient")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </aside>

        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form">
            {/* ── Header : titre ── */}
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {t("signup.createAccountTitle1", "Create Your")}{" "}
                <span className="signup-form-title-accent">
                  {t("signup.createAccountTitle2", "Doctor Account")}
                </span>
              </h1>
            </div>

            <div className="signup-form-content">
              {/* ── Progress bar ── */}
              <div className="rf4-steps" aria-label="Progress indicator">
                <span className="rf4-step rf4-step--active" />
                <span className="rf4-step rf4-step--active" />
                <span className="rf4-step rf4-step--active" />
                <span className="rf4-step rf4-step--active" />
              </div>
              <p className="rf4-step-label">
                {t("signup.stepLabel4", "Step 4 / 4")}
              </p>

              {/* ── Titre de section + description ── */}
              <div className="rf4-section">
                <h2 className="rf4-heading">
                  {t("signup.doctor.step4Heading", "IDENTITY CONFIRMATION")}
                </h2>
                <p className="rf4-text">
                  {t(
                    "signup.doctor.step4Description",
                    "For security reasons, please provide a valid government ID. The name must match your medical license."
                  )}
                </p>
              </div>

              <div className="rf4-form">
                <div
                  className={`rf4-upload${identityImg ? " rf4-upload--has-file" : ""}`}
                  onDrop={onDropIdentity}
                  onDragOver={onDragOver}
                  role="region"
                  aria-label="Identity image upload"
                >
                  {identityImg && identityPreviewUrl ? (
                    <div className="rf4-preview-wrap">
                      <button
                        type="button"
                        className="rf4-preview-close"
                        onClick={handleRemoveIdentity}
                        aria-label="Remove image"
                      >
                        <CloseOutlined />
                      </button>
                      <img
                        src={identityPreviewUrl}
                        alt="Identity preview"
                        className="rf4-preview"
                      />
                    </div>
                  ) : (
                    <div className="rf4-upload-content">
                      <div className="rf4-icon">
                        <img src={uploadIcon} alt="" aria-hidden />
                      </div>
                      <p className="rf4-upload-title">
                        {t("signup.doctor.step4UploadTitle", "Drag Or Upload Your Photo")}
                      </p>
                      <p className="rf4-upload-subtitle">
                        {t(
                          "signup.doctor.step4UploadSubtitle",
                          "Upload a clear, valid copy of your medical license. This helps us ensure that all professionals on our platform are verified."
                        )}
                      </p>
                      <div className="rf4-upload-btn-wrap">
                        <label className="rf4-upload-btn">
                          {t("signup.doctor.uploadButtonPicture", "Upload Picture")}
                          <input
                            type="file"
                            accept="image/jpeg,image/png"
                            onChange={onChangeIdentity}
                            hidden
                          />
                        </label>
                      </div>
                      <p className="rf4-upload-hint">
                        {t(
                          "signup.doctor.uploadFormatsHint",
                          "Accepted formats: JPG, PNG — Max size: 5 MB."
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {licenceImg && (
                  <div className="rf4-chip rf4-chip--success">
                    <span className="rf4-chip-dot" aria-hidden>
                      ✓
                    </span>
                    {t("signup.doctor.licenseFileLabel", "Licence file")} :{" "}
                    <span title={licenceImg.name || "license"}>
                      {t("signup.doctor.licenseFileAttached", "Attached")}
                    </span>
                  </div>
                )}

                {!!error && (
                  <div className="rf4-error">
                    <Alert
                      className="rf4-alert"
                      type="error"
                      message={(() => {
                        if (typeof error === "string" && error.trim()) {
                          const trimmed = error.trim();
                          const detail = trimmed === "Registration failed"
                            ? t("signup.doctor.registrationEmailInUse")
                            : trimmed;
                          return `${t("signup.doctor.registrationErrorPrefix")}${detail}`;
                        }
                        return t("signup.doctor.registrationErrorTitle");
                      })()}
                      showIcon
                      closable
                      onClose={() => setError(null)}
                    />
                  </div>
                )}

                {loading && (
                  <div className="rf4-loading">{t("signup.doctor.submitting")}</div>
                )}

                <button
                  className="rf4-button-container"
                  disabled={!isValid || loading}
                  onClick={() => handleSubmit()}
                  type="button"
                >
                  {t("commonn.continue", "Continue")}
                </button>

                <div className="signup-login-link-wrapper">
                  <span className="signup-login-text">
                    {t("signup.alreadyHaveAccount2", "You already have an active account ?")}{" "}
                    <span className="signup-login-link" onClick={() => navigate("/login")}>
                      {t("commonn.signInNow", "Sign in now !")}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}